import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Input, SearchableSelect } from '../../../../components/ui';
import { useFeatureTheme } from '../../../../theme/hooks/useFeatureTheme';
import { ANALYTICS_COPY } from '../../constants/analyticsCopy';
import {
  CUSTOM_DATE_PRESET,
  DATE_PRESET_IDS,
  REPORT_DEFAULT_PRESET,
  datePresetLabels,
} from '../../utils/datePresets';
import { kolkataToday, addDays } from '../../utils/previousPeriod';

const LABELS = datePresetLabels(ANALYTICS_COPY);
const SORT_OPTIONS = [
  { id: '', label: 'Default order' },
  { id: 'desc', label: 'Highest to lowest' },
  { id: 'asc', label: 'Lowest to highest' },
];

const OPTIONS = [
  { id: REPORT_DEFAULT_PRESET, label: LABELS.report_default },
  ...DATE_PRESET_IDS.map((id) => ({ id, label: LABELS[id] })),
  { id: CUSTOM_DATE_PRESET, label: LABELS.custom },
];

export default function DashboardDateControl({
  value,
  onChange,
  sortOrder = '',
  onSortOrderChange,
}) {
  const { semantic, typography } = useFeatureTheme();
  const selected = OPTIONS.find((item) => item.id === value?.preset) || OPTIONS[0];
  const invalid = Boolean(value?.start && value?.end && value.end < value.start);

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          alignItems: 'flex-end',
        }}
      >
        <Box sx={{ minWidth: 220, maxWidth: 280, flex: '1 1 220px' }}>
          <SearchableSelect
            label={ANALYTICS_COPY.builderStep2DateLabel}
            options={OPTIONS}
            value={selected}
            onChange={(option) => {
              if (!option?.id || option.id === value?.preset) return;
              if (option.id === CUSTOM_DATE_PRESET) {
                onChange({ preset: CUSTOM_DATE_PRESET, start: '', end: '' });
                return;
              }
              if (option.id === 'today') {
                const today = kolkataToday();
                onChange({ preset: 'today', start: today, end: today });
                return;
              }
              if (option.id === 'yesterday') {
                const yDay = addDays(kolkataToday(), -1);
                onChange({ preset: 'yesterday', start: yDay, end: yDay });
                return;
              }
              onChange({ preset: option.id });
            }}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            clearable={false}
          />
        </Box>
        {value?.preset === CUSTOM_DATE_PRESET ? (
          <>
            <Box sx={{ minWidth: 180, flex: '1 1 180px' }}>
              <Input
                label={ANALYTICS_COPY.dateRangeLabel}
                type="date"
                value={value.start || ''}
                error={invalid}
                inputProps={{ max: value.end || undefined }}
                onChange={(event) => onChange({ ...value, start: event.target.value })}
              />
            </Box>
            <Box sx={{ minWidth: 180, flex: '1 1 180px' }}>
              <Input
                label={ANALYTICS_COPY.dateRangeEndLabel}
                type="date"
                value={value.end || ''}
                error={invalid}
                errorMessage={invalid ? ANALYTICS_COPY.dateRangeInvalid : ''}
                inputProps={{ min: value.start || undefined }}
                onChange={(event) => onChange({ ...value, end: event.target.value })}
              />
            </Box>
          </>
        ) : null}
        <Box sx={{ ml: 'auto', minWidth: 220, maxWidth: 280, flex: '0 1 280px' }}>
          <SearchableSelect
            label="Sort values"
            placeholder="Choose sort order"
            options={SORT_OPTIONS}
            value={SORT_OPTIONS.find((item) => item.id === sortOrder) || SORT_OPTIONS[0]}
            onChange={(option) => onSortOrderChange?.(option?.id || '')}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            clearable={false}
          />
        </Box>
      </Box>
      <Typography sx={{ fontSize: typography.fontSizeMd, color: semantic.text.secondary }}>
        {value?.preset === CUSTOM_DATE_PRESET
          ? ANALYTICS_COPY.datePresetCustomHint
          : ANALYTICS_COPY.datePresetHint}
      </Typography>
    </Stack>
  );
}
